/**
 * This file is part of the Valkyrie Robotics public website.
 * © 2025 Valkyrie Robotics, LLC. All rights reserved.
 */

import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getCalApi } from "@calcom/embed-react";
import { motion } from "framer-motion";
import { SEO } from "../components/SEO";
import { FreeTrialSchedule } from "../components/FreeTrialSchedule";

const modules = import.meta.glob("/content/blog/**/*.mdx", {
  eager: true,
}) as Record<string, any>;

function findPost(slug: string) {
  const entry = Object.entries(modules).find(
    ([path]) => path.endsWith(`/${slug}.mdx`) || path.endsWith(`/${slug}.md`)
  );
  if (!entry) return null;
  const [_, mod] = entry;
  return mod.default ? mod : null;
}

export function BlogPostPage() {
  const { slug } = useParams();

  if (!slug) return null;
  const mod = findPost(slug);
  if (!mod) {
    return (
      <>
        <SEO
          title="Post Not Found"
          description="The blog post you're looking for doesn't exist or has been moved."
          noindex={true}
        />
        <div className="bg-neutral-900 min-h-screen">
          <section className="mx-auto max-w-6xl px-4 py-24 text-gray-100">
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white">
                Post Not Found
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                The blog post you're looking for doesn't exist or has been
                moved.
              </p>
              <Link
                to="/blog"
                className="inline-block rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
              >
                Back to Blog
              </Link>
            </div>
          </section>
        </div>
      </>
    );
  }
  const MDX = mod.default;
  const meta = mod.meta || {};

  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "free-trial" });
      cal("ui", {
        hideEventTypeDetails: false,
        layout: "month_view",
        theme: "dark",
        styles: { branding: { brandColor: "#c4a23a" } },
      });
    })();
  }, []);

  return (
    <>
      <SEO
        title={meta.title || "Blog Post"}
        description={
          meta.excerpt ||
          "Valkyrie Robotics blog post about VEX Robotics and STEM education."
        }
        canonical={`/blog/${slug}`}
        type="article"
        publishedTime={meta.date}
        modifiedTime={meta.date}
        image={meta.image}
        keywords={[
          "VEX Robotics",
          "robotics education",
          "STEM learning",
          "competitive robotics",
          "robotics coaching",
          "student robotics",
          "robotics blog",
        ]}
      />
      <div className="bg-neutral-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 py-24 text-gray-100">
            <div className="text-center space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-4"
              >
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 transition-colors duration-300 mb-8"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back to Blog
                </Link>

                {meta.title && (
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white max-w-4xl mx-auto leading-tight">
                    {meta.title}
                  </h1>
                )}

                {meta.date && (
                  <p className="text-brand-400 font-medium">
                    {new Date(meta.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="mx-auto max-w-4xl px-4 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <article className="prose prose-invert prose-neutral prose-lg max-w-none [&_a]:text-brand-400 [&_a]:hover:text-brand-300 [&_a]:underline [&_a]:transition-colors [&_a]:duration-300">
              <div className="bg-neutral-800/30 rounded-2xl p-8 md:p-12 border border-neutral-700">
                <MDX />
              </div>
            </article>
          </motion.div>

          {/* Call to Action Section */}
          <motion.div
            className="mt-20 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
              <h3 className="text-2xl font-bold tracking-tight mb-4 text-gray-100">
                Ready to Get Started?
              </h3>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
                Have questions about VEX Robotics or want to learn more about
                our programs? Book a free trial session or contact us to discuss
                how we can help your child succeed.
              </p>
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <motion.button
                    data-cal-namespace="free-trial"
                    data-cal-link="aplakosh/free-trial"
                    data-cal-config='{"layout":"month_view"}'
                    className="w-full sm:w-auto rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Book Free Trial Session
                  </motion.button>
                  <motion.a
                    href="/contact"
                    className="w-full sm:w-auto rounded-lg border-2 border-neutral-600 text-gray-200 px-8 py-4 text-lg font-medium transition-all duration-150 hover:scale-105 hover:border-brand-500 hover:text-brand-400 active:scale-95"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Contact Us
                  </motion.a>
                </div>
                <FreeTrialSchedule />
              </div>
            </div>
          </motion.div>

          {/* Related Posts Section */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold tracking-tight mb-4 text-gray-100">
                More Articles
              </h3>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Explore more insights and guides about VEX Robotics and STEM
                education.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  slug: "what-is-robotics",
                  image: "/media/blogs/what-is-vex-robotics.png",
                  title: "What Is VEX Robotics?",
                  category: "Getting Started",
                },
                {
                  slug: "why-vex-robotics-gives-students-an-edge-on-college-applications",
                  image: "/media/blogs/why-vex-robotics.png",
                  title: "College Applications Edge",
                  category: "College Prep",
                },
                {
                  slug: "which-program-is-right-for-my-child",
                  image: "/media/blogs/why-we-started-valkyrie-robotics.png",
                  title: "VEX IQ vs. V5 Guide",
                  category: "Program Guide",
                },
              ].map((post, index) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="group block rounded-xl border border-neutral-800 bg-neutral-900/50 hover:border-neutral-700 hover:bg-neutral-800/30 transition-all duration-300 overflow-hidden">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="block w-full h-full"
                    >
                      <div className="aspect-[16/9] overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                        <img
                          src={post.image}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src =
                              "/media/hero.jpg";
                          }}
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block px-3 py-1 text-xs font-medium text-brand-400 bg-neutral-900/80 rounded-full border border-neutral-700">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h4 className="text-lg font-semibold tracking-tight text-gray-100 group-hover:text-white transition-colors duration-300 mb-2">
                          {post.title}
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="text-brand-400 text-sm font-medium group-hover:text-brand-300 transition-colors duration-300">
                            Read Article →
                          </span>
                          <div className="w-5 h-5 text-brand-400 group-hover:translate-x-1 transition-transform duration-300">
                            <svg
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
}
